import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IMetaPage, IMetaSettings, OmniCampaign, TPublisherPlatforms } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';

/**
 * Platform option for publisher platforms dropdown.
 */
export interface IPlatformOption {
  id: string;
  name: string;
  platforms: TPublisherPlatforms[];
  isDisabled: boolean;
  tooltip?: ReactNode;
}

/**
 * Return type for the useMetaChannelSettingsCard hook.
 */
export interface IUseMetaChannelSettingsCardResult {
  /** Available Facebook pages for selection */
  facebookPages: IMetaPage[];
  /** Whether Facebook pages are currently loading */
  isLoadingFacebookPages: boolean;
  /** Currently selected Facebook page */
  selectedFacebookPage: IMetaPage | undefined;
  /** Handler for Facebook page selection */
  handleFacebookPageSelect: (page: IMetaPage) => void;
  /** Whether the selected page has an Instagram account */
  hasSingleAccount: boolean;
  /** Base platform options before Instagram permission filtering */
  basePlatformOptions: IPlatformOption[];
  /** Whether the selected page has Instagram permissions */
  hasInstagramPermissions: boolean;
  /** Currently selected platform option */
  selectedPlatform: IPlatformOption | undefined;
  /** Handler for publisher platform selection */
  handlePublisherPlatformsSelect: (option: IPlatformOption) => void;
}

/**
 * Hook for managing Meta channel settings card state and handlers.
 *
 * Handles:
 * - Facebook page selection and validation
 * - Publisher platform options (Facebook only, Instagram only, both)
 * - Special ad category selection
 *
 * @param campaign - The OmniCampaign instance to read/write settings from/to
 * @returns State and handlers for all Meta settings fields
 *
 * @example
 * ```tsx
 * const { facebookPages, selectedFacebookPage, handleFacebookPageSelect } =
 *   useMetaChannelSettingsCard(campaign);
 * ```
 */
export function useMetaChannelSettingsCard(
  campaign: OmniCampaign,
): IUseMetaChannelSettingsCardResult {
  const { MetaIntegrations } = useStore();
  const { t } = useTranslation();

  const metaSettings: Partial<IMetaSettings> = campaign.get('meta_settings') || {};

  const [facebookPages, setFacebookPages] = useState<IMetaPage[]>([]);
  const [isLoadingFacebookPages, setIsLoadingFacebookPages] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    async function fetchPages(): Promise<void> {
      setIsLoadingFacebookPages(true);
      try {
        const integration = await MetaIntegrations.fetchIntegration();
        if (controller.signal.aborted) {
          return;
        }
        const pages = await integration.getPages();
        if (controller.signal.aborted) {
          return;
        }
        setFacebookPages(pages);
      } catch {
        if (!controller.signal.aborted) {
          setFacebookPages([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingFacebookPages(false);
        }
      }
    }

    fetchPages();

    return () => {
      controller.abort();
    };
  }, [MetaIntegrations]);

  const selectedFacebookPage = useMemo((): IMetaPage | undefined => {
    if (!metaSettings.page_id) {
      return undefined;
    }
    return facebookPages.find((p: IMetaPage): boolean => p.id === metaSettings.page_id);
  }, [facebookPages, metaSettings.page_id]);

  const hasSingleAccount =
    selectedFacebookPage?.instagram_account_id !== null &&
    selectedFacebookPage?.instagram_account_id !== undefined;

  const basePlatformOptions: IPlatformOption[] = [
    {
      id: 'facebook_only',
      name: t('Facebook only'),
      platforms: ['facebook', 'messenger', 'audience_network'],
      isDisabled: false,
    },
    {
      id: 'instagram_only',
      name: t('Instagram only'),
      platforms: ['instagram', 'messenger', 'audience_network'],
      isDisabled: false,
    },
    {
      id: 'facebook_and_instagram',
      name: t('Facebook and Instagram'),
      platforms: ['facebook', 'instagram', 'messenger', 'audience_network'],
      isDisabled: false,
    },
  ];

  const hasInstagramPermissions =
    selectedFacebookPage?.instagram_account_id !== null &&
    selectedFacebookPage?.instagram_account_id !== undefined;

  const selectedPlatform = useMemo((): IPlatformOption | undefined => {
    if (!metaSettings.publisher_platforms) {
      return undefined;
    }
    const platforms = metaSettings.publisher_platforms;
    return basePlatformOptions.find(
      (option: IPlatformOption): boolean =>
        option.platforms.length === platforms.length &&
        option.platforms.every((p: TPublisherPlatforms): boolean => platforms.includes(p)),
    );
  }, [metaSettings.publisher_platforms, basePlatformOptions]);

  /**
   * Handles Facebook page selection.
   * Updates meta_settings.page_id and clears publisher platforms if Instagram status changes.
   *
   * @param page - The selected Facebook page
   */
  function handleFacebookPageSelect(page: IMetaPage): void {
    const hasInstagram =
      page.instagram_account_id !== null && page.instagram_account_id !== undefined;
    const currentHasInstagram =
      selectedFacebookPage?.instagram_account_id !== null &&
      selectedFacebookPage?.instagram_account_id !== undefined;

    const updatedSettings: Partial<IMetaSettings> = {
      ...metaSettings,
      page_id: page.id,
    };

    if (hasInstagram !== currentHasInstagram) {
      updatedSettings.publisher_platforms = undefined;
    }

    campaign.set({ meta_settings: updatedSettings as IMetaSettings });
  }

  /**
   * Handles publisher platform selection.
   * Updates meta_settings.publisher_platforms with the selected platforms array.
   *
   * @param option - The selected platform option
   */
  function handlePublisherPlatformsSelect(option: IPlatformOption): void {
    campaign.set({
      meta_settings: {
        ...metaSettings,
        publisher_platforms: option.platforms,
      } as IMetaSettings,
    });
  }

  return {
    facebookPages,
    isLoadingFacebookPages,
    selectedFacebookPage,
    handleFacebookPageSelect,
    hasSingleAccount,
    basePlatformOptions,
    hasInstagramPermissions,
    selectedPlatform,
    handlePublisherPlatformsSelect,
  };
}
